import { motion } from 'framer-motion';
import { useRef, useState } from 'react';

const clients = [
  { name: 'Jersey Joint Dispensary', logo: '/images/clients/jersey-joint.png' },
  { name: 'Warped Tour', logo: '/images/clients/warped-tour.png' },
  { name: "Jay & Silent Bob's Secret Stash", logo: '/images/clients/secret-stash.png', size: 220 },
  { name: 'Front Sight Coffee', logo: '/images/clients/front-sight-coffee.jpeg', roundCrop: true },
  { name: 'Aperture Slate', logo: '/images/clients/aperture-slate.jpeg', size: 280 },
  { name: 'Rise My Rank', logo: '/images/clients/rise-my-rank.webp' },
];

const GLOW_IDLE   = 'grayscale(1) drop-shadow(0 0 12px rgba(168,85,247,0.55)) drop-shadow(0 0 30px rgba(147,51,234,0.3))';
const GLOW_HOVER  = 'grayscale(0) drop-shadow(0 0 22px rgba(168,85,247,1)) drop-shadow(0 0 55px rgba(147,51,234,0.8))';

function ClientLogo({ name, logo, roundCrop, size = 160 }) {
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="flex-none flex items-center justify-center px-10 py-4 cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {roundCrop ? (
        <div
          className="rounded-full overflow-hidden flex-none transition-all duration-500"
          style={{
            width: 160,
            height: 160,
            boxShadow: hovered
              ? '0 0 25px rgba(168,85,247,1), 0 0 60px rgba(147,51,234,0.7)'
              : '0 0 14px rgba(168,85,247,0.5), 0 0 35px rgba(147,51,234,0.25)',
          }}
        >
          <img
            src={logo}
            alt={name}
            className="w-full h-full object-cover transition-all duration-500"
            style={{ filter: hovered ? 'grayscale(0)' : 'grayscale(1)', opacity: hovered ? 1 : 0.55 }}
            onError={(e) => { e.target.parentElement.style.display = 'none'; }}
          />
        </div>
      ) : (
        <img
          src={logo}
          alt={name}
          className="w-auto object-contain transition-all duration-500"
          style={{
            height: size,
            maxWidth: size * 1.6,
            filter: hovered ? GLOW_HOVER : GLOW_IDLE,
            opacity: hovered ? 1 : 0.55,
          }}
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      )}
    </motion.div>
  );
}

export default function ClientLogos() {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 340, behavior: 'smooth' });
  };

  return (
    <section className="border-y border-white/10 py-16 md:py-20 bg-black">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-10">
        <p className="text-center text-xs tracking-[0.3em] uppercase text-white/30 mb-10 font-medium">
          Trusted by
        </p>

        <div className="relative flex items-center gap-3">
          <button
            onClick={() => scroll(-1)}
            aria-label="Scroll left"
            className="flex-none z-10 flex items-center justify-center w-10 h-10 rounded-full border border-white/20 text-white/50 hover:border-purple-400 hover:text-purple-400 transition-all duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z" clipRule="evenodd" />
            </svg>
          </button>

          <div
            ref={scrollRef}
            className="flex items-center overflow-x-auto [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: 'none' }}
          >
            {clients.map((client, i) => (
              <ClientLogo key={i} {...client} />
            ))}
          </div>

          <button
            onClick={() => scroll(1)}
            aria-label="Scroll right"
            className="flex-none z-10 flex items-center justify-center w-10 h-10 rounded-full border border-white/20 text-white/50 hover:border-purple-400 hover:text-purple-400 transition-all duration-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
}
